<script>
  import { loadStripe } from '@stripe/stripe-js';
  import { onMount, tick } from 'svelte';
  import { navigate } from 'svelte-routing';
  import { getApiUrl, getStripeUrl } from '../api';
  import { authStore } from '../authStore';
  import { selectedCountry } from '../countryStore';
  import { getCurrencySymbol } from '../utils';
  import { authFetch } from '../authService'; // <-- IMPORT THIS

  const STRIPE_KEY = getStripeUrl();
  const stripePromise = loadStripe(STRIPE_KEY);
  
  let currentUser;
  let stripe;
  let elements;
  
  // State
  let selectedPackId = null;
  let isLoading = false;
  let isProcessing = false;
  let error = '';
  
  // Configuration matches Backend
  const packs = [
      { credits: 50, price: 50, id: 'pack_50' },
      { credits: 100, price: 90, id: 'pack_100', badge: '10% OFF' }, 
      { credits: 500, price: 400, id: 'pack_500', badge: 'BEST VALUE' }
  ];

  let currencyCode = 'HKD';
  let currencySymbol = '$';

  $: if ($selectedCountry) {
      currencyCode = $selectedCountry === 'GB' ? 'GBP' : $selectedCountry === 'JP' ? 'JPY' : 'HKD';
      currencySymbol = getCurrencySymbol($selectedCountry);
  }

  authStore.subscribe(v => currentUser = v.user);

  onMount(async () => {
    stripe = await stripePromise;
  });

  async function selectPack(pack) {
      if (selectedPackId === pack.id) return; // Already selected
      
      selectedPackId = pack.id;
      isLoading = true;
      error = '';
      elements = null; // Reset previous elements

      try {
          // 1. Get Client Secret
          const res = await authFetch(getApiUrl('/credits/intent'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ 
                  packId: pack.id,
                  currency: currencyCode 
              })
          });
          
          const data = await res.json();
          if(!res.ok) throw new Error(data.error);

          // 2. Initialize Stripe
          elements = stripe.elements({ clientSecret: data.clientSecret });
          const paymentElement = elements.create('payment');
          
          // --- CRITICAL FIX START ---
          // 1. Unhide the container first
          isLoading = false; 
          // 2. Wait for Svelte to update the DOM and render <div id="payment-element">
          await tick(); 
          // 3. Now safe to mount
          paymentElement.mount('#payment-element');
          // --- CRITICAL FIX END ---
          
      } catch(e) {
          error = e.message;
          isLoading = false; // Ensure loading stops on error
      }
  }

  async function confirmPayment() {
      if (!stripe || !elements) return;
      isProcessing = true;
      error = '';

      const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
          elements,
          confirmParams: { return_url: window.location.origin + '/dashboard' },
          redirect: 'if_required'
      });

      if (stripeError) {
          error = stripeError.message;
          isProcessing = false;
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
          try {
              // 3. Confirm with Backend to Add Credits
              const res = await authFetch(getApiUrl('/credits/purchase'), {
                  method: 'POST',
                  headers: {'Content-Type': 'application/json'},
                  body: JSON.stringify({ 
                      userId: currentUser.idTokenClaims.oid,
                      paymentIntentId: paymentIntent.id,
                      packId: selectedPackId
                  })
              });
              
              if(!res.ok) throw new Error("Payment succeeded, but credit update failed.");
              
              alert("Credits added successfully!");
              navigate('/dashboard');

          } catch(e) {
              error = e.message;
              isProcessing = false;
          }
      }
  }
</script>

<div class="topup-container">
    <h2>Buy Credits</h2>
    <p class="subtitle">Get more credits to post items or boost promotions.</p>

    <div class="grid">
        {#each packs as pack}
            <div 
                class="card {selectedPackId === pack.id ? 'selected' : ''}" 
                on:click={() => selectPack(pack)}
            >
                {#if pack.badge}
                    <span class="badge">{pack.badge}</span>
                {/if}
                <h3>{pack.credits} Credits</h3>
                <p class="price">{currencySymbol}{pack.price}</p>
                
                {#if selectedPackId === pack.id}
                     <div class="active-indicator">Selected</div>
                {/if}
            </div>
        {/each}
    </div>

    <!-- Payment Section -->
    {#if selectedPackId}
        <div class="checkout-section">
            <h3>Complete Purchase</h3>
            {#if isLoading}
                <p>Loading secure payment...</p>
            {:else}
                <!-- This div must exist before mount() is called -->
                <div id="payment-element"></div>
                
                {#if error}
                    <p class="error">{error}</p>
                {/if}

                <div class="actions">
                    <button class="pay-btn" on:click={confirmPayment} disabled={isProcessing}>
                        {isProcessing ? 'Processing...' : 'Pay Now'}
                    </button>
                </div>
            {/if}
        </div>
    {/if}
</div>

<style>
    .topup-container { max-width: 800px; margin: 2rem auto; text-align: center; padding: 0 1rem; }
    .subtitle { color: #666; margin-bottom: 2rem; }
    
    .grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1.5rem; margin-bottom: 2rem; }
    
    .card { 
        background: white; padding: 2rem; border-radius: 12px; 
        box-shadow: 0 4px 10px rgba(0,0,0,0.05); cursor: pointer;
        border: 2px solid transparent; transition: all 0.2s;
        position: relative;
    }
    .card:hover { transform: translateY(-5px); box-shadow: 0 8px 20px rgba(0,0,0,0.1); }
    .card.selected { border-color: #007bff; background-color: #f8fbff; }

    .price { font-size: 2rem; font-weight: bold; color: #007bff; margin: 0.5rem 0; }
    
    .badge {
        position: absolute; top: -10px; right: 10px;
        background: #28a745; color: white; font-size: 0.8rem; font-weight: bold;
        padding: 4px 10px; border-radius: 12px;
    }

    .active-indicator { color: #007bff; font-weight: bold; margin-top: 1rem; }

    /* Checkout Area */
    .checkout-section {
        background: white; padding: 2rem; border-radius: 12px; 
        box-shadow: 0 4px 15px rgba(0,0,0,0.1); text-align: left;
        max-width: 500px; margin: 0 auto;
    }
    
    .pay-btn {
        width: 100%; padding: 1rem; background-color: #007bff; color: white;
        border: none; border-radius: 6px; font-size: 1.1rem; font-weight: bold;
        cursor: pointer; margin-top: 1.5rem;
    }
    .pay-btn:disabled { background-color: #ccc; cursor: not-allowed; }
    
    .error { color: #dc3545; margin-top: 1rem; font-weight: bold; }
</style>